import { useState, useEffect } from "react";
import { DataTable, ConfirmDialog } from "../../components/dashboard";
import { listPlans, createPlan, updatePlan, deletePlan } from "../../lib/api/cloud";
import type { ResourcePlan } from "../../types/cloud";

interface PlanFormData {
  name: string;
  cpuCores: number;
  ramMb: number;
  diskGb: number;
  maxProjects: number;
  providerPresetId: string;
  active: boolean;
}

const EMPTY_FORM: PlanFormData = {
  name: "",
  cpuCores: 2,
  ramMb: 4096,
  diskGb: 50,
  maxProjects: 10,
  providerPresetId: "",
  active: true,
};

export function PlanManagement() {
  const [plans, setPlans] = useState<ResourcePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<ResourcePlan | null>(null);
  const [formData, setFormData] = useState<PlanFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ResourcePlan | null>(null);

  const fetchPlans = async () => {
    try {
      const res = await listPlans();
      setPlans(res.plans);
    } catch {
      // Error handling
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const openCreate = () => {
    setEditingPlan(null);
    setFormData(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (plan: ResourcePlan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      cpuCores: plan.cpuCores,
      ramMb: plan.ramMb,
      diskGb: plan.diskGb,
      maxProjects: plan.maxProjects,
      providerPresetId: plan.providerPresetId ?? "",
      active: plan.active,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        ...formData,
        providerPresetId: formData.providerPresetId || undefined,
      };

      if (editingPlan) {
        await updatePlan(editingPlan.id, data);
      } else {
        await createPlan(data);
      }
      await fetchPlans();
      setShowForm(false);
    } catch {
      // Error handling
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deletePlan(deleteTarget.id);
      await fetchPlans();
    } catch {
      // Error handling
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleToggleActive = async (plan: ResourcePlan) => {
    try {
      await updatePlan(plan.id, { active: !plan.active });
      await fetchPlans();
    } catch {
      // Error handling
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const columns = [
    {
      key: "name",
      label: "Name",
      sortable: true,
      render: (p: ResourcePlan) => <span className="font-medium text-sm">{p.name}</span>,
    },
    {
      key: "cpuCores",
      label: "CPU",
      sortable: true,
      render: (p: ResourcePlan) => <span className="text-sm">{p.cpuCores} cores</span>,
    },
    {
      key: "ramMb",
      label: "RAM",
      sortable: true,
      render: (p: ResourcePlan) => <span className="text-sm">{p.ramMb / 1024} GB</span>,
    },
    {
      key: "diskGb",
      label: "Disk",
      sortable: true,
      render: (p: ResourcePlan) => <span className="text-sm">{p.diskGb} GB</span>,
    },
    {
      key: "maxProjects",
      label: "Max Projects",
      render: (p: ResourcePlan) => <span className="text-sm">{p.maxProjects}</span>,
    },
    {
      key: "active",
      label: "Active",
      render: (p: ResourcePlan) => (
        <button
          onClick={() => handleToggleActive(p)}
          className={`w-10 h-5 rounded-full relative transition-colors ${
            p.active ? "bg-green-500/30" : "bg-[#333]"
          }`}
        >
          <span
            className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${
              p.active ? "left-5 bg-green-400" : "left-0.5 bg-[#666]"
            }`}
          />
        </button>
      ),
    },
    {
      key: "userCount",
      label: "Users",
      render: (p: ResourcePlan) => <span className="text-[#999] text-sm">{p.userCount}</span>,
    },
    {
      key: "actions",
      label: "Actions",
      render: (p: ResourcePlan) => (
        <div className="flex gap-2">
          <button
            onClick={() => openEdit(p)}
            className="px-2.5 py-1 bg-blue-500/10 text-blue-400 rounded text-xs font-medium hover:bg-blue-500/20"
          >
            Edit
          </button>
          {p.userCount === 0 && (
            <button
              onClick={() => setDeleteTarget(p)}
              className="px-2.5 py-1 bg-red-500/10 text-red-400 rounded text-xs font-medium hover:bg-red-500/20"
            >
              Delete
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#f5f5f5]">Resource Plans</h1>
          <p className="text-[#999] text-sm mt-1">{plans.length} plans configured</p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-400 hover:to-purple-500 text-white rounded-lg text-sm font-medium transition-all"
        >
          Create Plan
        </button>
      </div>

      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg">
        <DataTable
          columns={columns}
          data={plans}
          keyFn={(p) => p.id}
          emptyMessage="No plans configured"
        />
      </div>

      {/* Create/Edit form dialog */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#1a1a1a] border border-[#333] rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-[#f5f5f5] mb-4">
              {editingPlan ? "Edit Plan" : "Create Plan"}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-[#999] text-xs mb-1">Plan Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-[#111] border border-[#333] rounded text-[#f5f5f5] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  placeholder="e.g., Standard"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#999] text-xs mb-1">CPU Cores</label>
                  <input
                    type="number"
                    value={formData.cpuCores}
                    onChange={(e) =>
                      setFormData({ ...formData, cpuCores: parseInt(e.target.value) || 1 })
                    }
                    min={1}
                    className="w-full px-3 py-2 bg-[#111] border border-[#333] rounded text-[#f5f5f5] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>
                <div>
                  <label className="block text-[#999] text-xs mb-1">RAM (MB)</label>
                  <input
                    type="number"
                    value={formData.ramMb}
                    onChange={(e) =>
                      setFormData({ ...formData, ramMb: parseInt(e.target.value) || 256 })
                    }
                    min={256}
                    step={256}
                    className="w-full px-3 py-2 bg-[#111] border border-[#333] rounded text-[#f5f5f5] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>
                <div>
                  <label className="block text-[#999] text-xs mb-1">Disk (GB)</label>
                  <input
                    type="number"
                    value={formData.diskGb}
                    onChange={(e) =>
                      setFormData({ ...formData, diskGb: parseInt(e.target.value) || 10 })
                    }
                    min={10}
                    className="w-full px-3 py-2 bg-[#111] border border-[#333] rounded text-[#f5f5f5] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>
                <div>
                  <label className="block text-[#999] text-xs mb-1">Max Projects</label>
                  <input
                    type="number"
                    value={formData.maxProjects}
                    onChange={(e) =>
                      setFormData({ ...formData, maxProjects: parseInt(e.target.value) || 1 })
                    }
                    min={1}
                    className="w-full px-3 py-2 bg-[#111] border border-[#333] rounded text-[#f5f5f5] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[#999] text-xs mb-1">Provider Preset ID</label>
                <input
                  type="text"
                  value={formData.providerPresetId}
                  onChange={(e) => setFormData({ ...formData, providerPresetId: e.target.value })}
                  className="w-full px-3 py-2 bg-[#111] border border-[#333] rounded text-[#f5f5f5] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  placeholder="Optional"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="rounded bg-[#111] border-[#333]"
                  id="plan-active"
                />
                <label htmlFor="plan-active" className="text-[#999] text-sm">
                  Active (can be assigned to users)
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm font-medium text-[#999] hover:text-[#f5f5f5]"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!formData.name || saving}
                className="px-4 py-2 bg-blue-500/10 text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-500/20 disabled:opacity-40"
              >
                {saving ? "Saving..." : editingPlan ? "Update Plan" : "Create Plan"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete Plan"
        message={`Are you sure you want to delete the plan "${deleteTarget?.name}"?`}
        confirmText="Delete Plan"
        danger
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
